import type { CardFinish, CardRarity } from "./types";

const TRAINER_SUBTYPES = new Set([
  "item",
  "supporter",
  "stadium",
  "tool",
  "pokémon tool",
  "pokemon tool",
  "technical machine",
  "ace spec",
  "rocket's secret machine",
  "goldenrod game corner",
  "team plasma",
]);

/**
 * Maps a TCG API rarity string to the canonical CardRarity enum.
 *
 * `supertype` takes precedence; when missing or unknown, `subtypes` is used as
 * a defensive fallback so modern cards whose rarity string is "Ultra Rare" /
 * "Double Rare" / etc. still tag as Trainer / Energy correctly.
 *
 * Called from every ingest path (web CSV importers, eBay file exchange,
 * pokemontcg.io API client, API server defense-in-depth on POST/PATCH).
 */
export function mapRarity(
  apiRarity?: string,
  supertype?: string,
  subtypes?: string[],
  hp?: string,
): CardRarity {
  const sup = supertype?.toLowerCase();
  if (sup === "trainer") return "Trainer";
  if (sup === "energy") return "Energy";
  // Subtype-based fallback when supertype is missing OR unrecognized
  // (don't override a known "Pokémon" supertype).
  if (sup !== "pokémon" && sup !== "pokemon") {
    const subs = (subtypes ?? []).map((s) => s.toLowerCase());
    if (subs.some((s) => TRAINER_SUBTYPES.has(s))) return "Trainer";
    // "Special" is unique to Special Energy in the TCG API schema.
    if (subs.includes("special")) return "Energy";
    // "Basic" appears on both Basic Pokémon and Basic Energy. Pokémon always
    // carry an `hp` value; basic energies do not — so a Basic-subtype card
    // with no HP is the Basic Energy case.
    if (subs.includes("basic") && !hp) return "Energy";
  }
  if (!apiRarity) return "Rare";
  const r = apiRarity.toLowerCase();
  if (r === "common") return "Common";
  if (r === "uncommon") return "Uncommon";
  // Magic's top pull tier. Game-agnostic passthrough: pokemontcg.io never
  // emits "mythic", so this only fires for an already-Magic rarity string
  // (Scryfall upstream, or a client/server round-trip of a normalized value),
  // and keeps Mythic distinct from Rare instead of coarsening it.
  if (r === "mythic" || r.includes("mythic")) return "Mythic";
  if (r.includes("trainer")) return "Trainer";
  if (r.includes("energy")) return "Energy";
  if (r.includes("promo")) return "Promo";
  // Everything Rare-and-above — Rare / Rare Holo / Ultra / Secret / V / ex /
  // VMAX / Illustration / Double Rare / Amazing / BREAK / etc. — collapses
  // to "Rare" at the rarity axis. The treatment dimension (Holo / Reverse
  // Holo / Premium) is carried by `Card.finish` via mapFinish.
  return "Rare";
}

/**
 * Print-treatment companion to mapRarity. Both consume the same upstream
 * rarity string, but mapFinish picks the second axis: what *treatment* this
 * specific printing has (Holo / Reverse Holo / Premium), or undefined for
 * the regular non-foil print.
 *
 * Trainer Gallery and the modern "Hit" tier (V/ex/VMAX/Ultra/Secret/etc.)
 * are treated as Premium since they have distinct full-art prints that
 * behave differently economically — even though their pull rarity might
 * also be Rare in the slimmed enum.
 */
export function mapFinish(
  apiRarity?: string,
  supertype?: string,
  subtypes?: string[],
  hp?: string,
): CardFinish | undefined {
  if (!apiRarity) return undefined;
  const r = apiRarity.toLowerCase();
  // Trainer / Energy supertype: usually non-foil; "Trainer Gallery Rare Holo"
  // is the standout exception and gets the Premium treatment.
  const sup = supertype?.toLowerCase();
  if (sup === "trainer" || sup === "energy") {
    if (r.includes("trainer gallery")) return "Premium";
    if (r.includes("holo")) return "Holo";
    return undefined;
  }
  if (r.includes("trainer gallery")) return "Premium";
  if (r.includes("reverse")) return "Reverse Holo";
  // Premium = the special premium treatments: gold / rainbow / hyper / secret /
  // illustration (illustration rare + special illustration rare) / alternative
  // art. Standard "rare" foils (ex/V/VMAX/VSTAR/GX/Ultra/Radiant/etc.) are
  // regular holo cards, not Premium.
  if (
    r.includes("gold") ||
    r.includes("rainbow") ||
    r.includes("hyper") ||
    r.includes("secret") ||
    r.includes("illustration") ||
    r.includes("alt")
  ) {
    return "Premium";
  }
  if (
    r === "rare holo" ||
    r.includes("holo") ||
    r.includes("ultra") ||
    r.includes("radiant") ||
    r.includes("vmax") ||
    r.includes("vstar") ||
    r.includes(" v") ||
    r.includes(" ex") ||
    r.includes(" gx") ||
    r.includes("double rare") ||
    r.includes("star") ||
    r.includes("amazing") ||
    r.includes("break") ||
    r.includes("prism") ||
    r.includes("legend") ||
    r.includes("shiny")
  ) {
    return "Holo";
  }
  void subtypes;
  void hp;
  return undefined;
}
